import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";

export default function Register() {
  const { register } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}`);
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="flex flex-col justify-between p-8 lg:p-12 border-r border-zinc-800">
      <div className="flex items-center gap-2">
        <LogoMark size={28} withGlow />
        <span className="font-display font-semibold text-white">Yosi Media Studio</span>
        <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            data-testid="register-lang-toggle"
            className="ml-auto text-xs uppercase tracking-widest text-zinc-400 hover:text-zinc-100"
          >
            {lang.toUpperCase()}
          </button>
        </div>
        <div className="max-w-md w-full mx-auto lg:mx-0 lg:mt-16">
          <div className="overline mb-3">{t("auth.register_subtitle")}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-100 leading-none">
            {t("auth.register_title")}
          </h2>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="overline block mb-1.5">{t("common.name")}</span>
              <input data-testid="register-name" required value={form.name} onChange={onChange("name")} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100" />
            </label>
            <label className="block">
              <span className="overline block mb-1.5">{t("auth.company_name")}</span>
              <input data-testid="register-company" value={form.company_name} onChange={onChange("company_name")} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100" placeholder={lang === "es" ? "Opcional — crea un tenant" : "Optional — creates a tenant"} />
            </label>
            <label className="block">
              <span className="overline block mb-1.5">{t("common.email")}</span>
              <input data-testid="register-email" type="email" required value={form.email} onChange={onChange("email")} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100" />
            </label>
            <label className="block">
              <span className="overline block mb-1.5">{t("common.password")}</span>
              <input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={onChange("password")} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100" />
            </label>
            <button data-testid="register-submit" disabled={loading} className="btn-scale w-full h-10 rounded-md bg-white text-zinc-950 font-medium hover:bg-zinc-100 flex items-center justify-center gap-2 disabled:opacity-70">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("auth.register_btn")}
            </button>
            <button type="button" onClick={google} data-testid="register-google" className="btn-scale w-full h-10 rounded-md border border-zinc-700 text-zinc-100 hover:bg-zinc-900">
              {t("auth.google_btn")}
            </button>
            <div className="text-center pt-2">
              <Link to="/login" data-testid="register-goto-login" className="text-sm text-zinc-400 hover:text-zinc-100 link-underline">
                {t("auth.go_login")}
              </Link>
            </div>
          </form>
        </div>
        <div className="text-[11px] text-zinc-600">© {new Date().getFullYear()} Yosi Media Studio</div>
      </div>
      <div className="relative hidden lg:block grid-bg">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/29506609/pexels-photo-29506609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", filter: "brightness(0.35) saturate(1.2)" }} />
      </div>
    </div>
  );
}
